import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiImage, FiCheck, FiCrop, FiZap } from 'react-icons/fi';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import axios from '../config/axios';
import { RARITIES, RARITY_LIST } from '../config/rarityConfig';

const GalleryImagePicker = ({ isOpen, onClose, onSelect, selectedImageUrl, defaultSource = 'challenge-gallery' }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [source, setSource] = useState(defaultSource); // 'challenges', 'gallery' or 'cartoon'
  const [rarityFilter, setRarityFilter] = useState('all');

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'general', label: 'General' },
    { value: 'nature', label: 'Nature' },
    { value: 'urban', label: 'Urban' },
    { value: 'historic', label: 'Historic' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'water', label: 'Water' },
    { value: 'park', label: 'Park' },
    { value: 'event', label: 'Event' },
    { value: 'people', label: 'People' }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchImages();
    }
  }, [isOpen, source]);

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !showCropModal) {
        onClose();
      }
    };

    if (isOpen && !showCropModal) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, showCropModal, onClose]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      let response;
      if (source === 'cartoon') {
        response = await axios.get('/cartoonify/nft-gallery');
        setImages(response.data || []);
      } else if (source === 'challenges') {
        response = await axios.get('/cms/challenge-images');
        setImages(response.data || []);
      } else if (source === 'challenge-gallery') {
        response = await axios.get('/cms/challenge-gallery');
        setImages(response.data || []);
      } else {
        response = await axios.get('/cms/gallery');
        setImages(response.data || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching images:', error);
      setImages([]);
      setLoading(false);
    }
  };

  const filteredImages = images.filter(image => {
    const matchesCategory = categoryFilter === 'all' || image.category === categoryFilter;
    const matchesSearch = image.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (image.description && image.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRarity = source !== 'cartoon' || rarityFilter === 'all' || image.rarity === rarityFilter;
    return matchesCategory && matchesSearch && matchesRarity;
  });

  const handleImageSelectWithCrop = (image) => {
    setSelectedImage(image);
    setShowCropModal(true);
  };

  const handleImageSelectWithoutCrop = (image) => {
    // Ensure we pass an absolute URL for the mobile app
    const baseUrl = process.env.REACT_APP_API_URL.replace('/api', '');
    const absoluteUrl = image.image_url.startsWith('http') ? image.image_url : `${baseUrl}${image.image_url}`;
    onSelect(absoluteUrl);
    onClose();
  };

  const handleRemoveImage = () => {
    onSelect('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                {source === 'cartoon' ? <FiZap className="w-5 h-5 text-white" /> : <FiImage className="w-5 h-5 text-white" />}
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                {source === 'cartoon' ? 'Select Cartoon Image' : 'Select Image from Gallery'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>

          {/* Source Tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => { setSource('challenge-gallery'); setRarityFilter('all'); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                source === 'challenge-gallery'
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FiImage className="w-4 h-4" />
              Challenge Gallery
            </button>
            <button
              onClick={() => { setSource('challenges'); setRarityFilter('all'); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                source === 'challenges'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FiImage className="w-4 h-4" />
              From Challenges
            </button>
            <button
              onClick={() => { setSource('gallery'); setRarityFilter('all'); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                source === 'gallery'
                  ? 'bg-secondary-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FiImage className="w-4 h-4" />
              Website Gallery
            </button>
            <button
              onClick={() => setSource('cartoon')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                source === 'cartoon'
                  ? 'bg-secondary-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FiZap className="w-4 h-4" />
              Cartoon Images
            </button>
          </div>

          {/* Rarity Filter (only for cartoon images) */}
          {source === 'cartoon' && (
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setRarityFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  rarityFilter === 'all'
                    ? 'bg-gray-700 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All Rarities
              </button>
              {RARITY_LIST.map(rarity => (
                <button
                  key={rarity.id}
                  onClick={() => setRarityFilter(rarity.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    rarityFilter === rarity.id
                      ? `${rarity.tailwind.badge}`
                      : `${rarity.tailwind.bg} ${rarity.tailwind.text} hover:opacity-80`
                  }`}
                >
                  {rarity.name}
                </button>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Image Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-12">
              <FiImage className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No images found</p>
              <p className="text-gray-400 text-sm mt-2">
                {searchTerm || categoryFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Upload images in the Gallery page first'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {filteredImages.map(image => {
                const baseUrl = process.env.REACT_APP_API_URL.replace('/api', '');
                const imageUrl = image.image_url.startsWith('http') ? image.image_url : `${baseUrl}${image.image_url}`;
                const thumbnailUrl = image.thumbnail_url
                  ? (image.thumbnail_url.startsWith('http') ? image.thumbnail_url : `${baseUrl}${image.thumbnail_url}`)
                  : imageUrl;
                const isSelected = selectedImageUrl && selectedImageUrl.includes(image.image_url);
                const rarityConfig = image.rarity ? RARITIES[image.rarity] : null;

                return (
                  <div
                    key={image.id}
                    className={`group relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 transition-all ${
                      isSelected
                        ? rarityConfig ? `ring-2` : 'ring-2 ring-primary-500'
                        : rarityConfig ? `hover:ring-${rarityConfig.id === 'legendary' ? 'amber' : rarityConfig.id === 'epic' ? 'purple' : rarityConfig.id === 'rare' ? 'blue' : 'gray'}-500` : 'hover:ring-primary-500'
                    }`}
                    style={isSelected && rarityConfig ? { borderColor: rarityConfig.color, boxShadow: `0 0 0 2px ${rarityConfig.color}` } : {}}
                  >
                    <img
                      src={thumbnailUrl}
                      alt={image.title || 'Image'}
                      className="w-full h-full object-cover"
                    />

                    {/* Rarity Badge (for cartoon images) */}
                    {rarityConfig && (
                      <div
                        className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-bold text-white"
                        style={{ backgroundColor: rarityConfig.color }}
                      >
                        {rarityConfig.name}
                      </div>
                    )}

                    {/* Overlay */}
                    <div className={`absolute inset-0 bg-black transition-opacity ${
                      isSelected ? 'bg-opacity-40' : 'bg-opacity-0 group-hover:bg-opacity-40'
                    }`}>
                      {/* Buttons */}
                      <div className={`absolute inset-0 flex flex-col items-center justify-center gap-2 transition-opacity ${
                        isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageSelectWithCrop(image);
                          }}
                          className="btn btn-primary text-sm flex items-center gap-2 text-sm"
                        >
                          <FiCrop className="w-3 h-3" />
                          Crop & Select
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageSelectWithoutCrop(image);
                          }}
                          className="px-3 py-1.5 bg-white text-gray-700 rounded-lg hover:bg-gray-100 flex items-center gap-2 text-sm"
                        >
                          <FiCheck className="w-3 h-3" />
                          Use As-Is
                        </button>
                      </div>

                      {/* Title */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs font-medium truncate">{image.title || 'Untitled'}</p>
                      </div>

                      {/* Selected Indicator */}
                      {isSelected && (
                        <div
                          className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: rarityConfig?.color || '#6366f1' }}
                        >
                          <FiCheck className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {filteredImages.length} {filteredImages.length === 1 ? 'image' : 'images'} available
            </p>
            <div className="flex gap-3">
              {selectedImageUrl && (
                <button
                  onClick={handleRemoveImage}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Remove Image
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Crop Modal */}
      {showCropModal && selectedImage && (
        <ImageCropModal
          image={selectedImage}
          onClose={() => {
            setShowCropModal(false);
            setSelectedImage(null);
          }}
          onSave={(imageUrl, cropData) => {
            onSelect(imageUrl, cropData);
            setShowCropModal(false);
            setSelectedImage(null);
            onClose();
          }}
        />
      )}
    </div>
  );
};

// Image Crop Modal Component
const ImageCropModal = ({ image, onClose, onSave }) => {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 80,
    height: 80,
    x: 10,
    y: 10
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(null);

  // ESC key handler for crop modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Helper to ensure URLs are absolute
  const toAbsoluteUrl = (url) => {
    if (!url) return url;
    if (url.startsWith('http')) return url;
    const baseUrl = process.env.REACT_APP_API_URL.replace('/api', '');
    return `${baseUrl}${url}`;
  };

  const handleSave = async () => {
    // If no crop or full image selected, just return original as absolute URL
    if (!completedCrop || (completedCrop.width >= 99 && completedCrop.height >= 99 && completedCrop.x <= 1 && completedCrop.y <= 1)) {
      onSave(toAbsoluteUrl(image.image_url));
      return;
    }

    // Get the actual image dimensions
    const imageWidth = imgRef.current?.naturalWidth || imgRef.current?.width || 1;
    const imageHeight = imgRef.current?.naturalHeight || imgRef.current?.height || 1;

    // Convert to percentages if in pixel units
    let cropData;
    if (completedCrop.unit === 'px') {
      cropData = {
        x: (completedCrop.x / imageWidth) * 100,
        y: (completedCrop.y / imageHeight) * 100,
        width: (completedCrop.width / imageWidth) * 100,
        height: (completedCrop.height / imageHeight) * 100
      };
    } else {
      cropData = {
        x: completedCrop.x,
        y: completedCrop.y,
        width: completedCrop.width,
        height: completedCrop.height
      };
    }

    // Call backend to apply crop
    setSaving(true);
    try {
      const response = await axios.post('/cms/crop-image', {
        imageUrl: image.image_url,
        cropData: cropData
      });

      if (response.data.success) {
        onSave(toAbsoluteUrl(response.data.imageUrl));
      } else {
        console.error('Crop failed:', response.data.error);
        onSave(toAbsoluteUrl(image.image_url)); // Fallback to original
      }
    } catch (error) {
      console.error('Error cropping image:', error);
      onSave(toAbsoluteUrl(image.image_url)); // Fallback to original
    } finally {
      setSaving(false);
    }
  };

  const presetCrops = [
    { name: 'Full Image', crop: { unit: '%', width: 100, height: 100, x: 0, y: 0 } },
    { name: 'Center Square', crop: { unit: '%', width: 80, height: 80, x: 10, y: 10 } },
    { name: 'Top Half', crop: { unit: '%', width: 100, height: 50, x: 0, y: 0 } },
    { name: 'Center', crop: { unit: '%', width: 100, height: 50, x: 0, y: 25 } },
    { name: 'Bottom Half', crop: { unit: '%', width: 100, height: 50, x: 0, y: 50 } },
    { name: 'Left Half', crop: { unit: '%', width: 50, height: 100, x: 0, y: 0 } },
    { name: 'Right Half', crop: { unit: '%', width: 50, height: 100, x: 50, y: 0 } }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Crop Image</h2>
            <p className="text-sm text-gray-600 mt-1">{image.title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Preset Buttons */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <p className="text-sm font-medium text-gray-700 mb-2">Quick Presets:</p>
          <div className="flex flex-wrap gap-2">
            {presetCrops.map((preset) => (
              <button
                key={preset.name}
                onClick={() => setCrop(preset.crop)}
                className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-primary-50 hover:border-primary-500 hover:text-primary-700 transition-colors"
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>

        {/* Crop Area */}
        <div className="flex-1 overflow-auto p-6 bg-gray-900 flex items-center justify-center">
          <div className="max-w-full max-h-full">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={undefined}
              className="max-w-full max-h-[60vh]"
            >
              <img
                ref={imgRef}
                src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${image.image_url}`}
                alt={image.title}
                className="max-w-full max-h-[60vh] object-contain"
              />
            </ReactCrop>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <p><strong>Instructions:</strong> Drag the crop box or use presets above</p>
            <p className="mt-1">The selected area will be displayed in the section</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Cropping...
                </>
              ) : (
                <>
                  <FiCheck className="w-4 h-4" />
                  Save & Apply
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryImagePicker;
